import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Building2,
  Calendar,
  CalendarDays,
  Check,
  ChevronRight,
  Clock,
  Flag,
  FolderKanban,
  GitBranch,
  History,
  Layers,
  Lightbulb,
  MessageSquare,
  Package,
  Paperclip,
  Pencil,
  Send,
  Trash2,
  User as UserIcon,
  X,
} from "lucide-react";
import { AppLayout } from "@/components/layout/AppLayout";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { ReferenceIdBadge } from "@/components/ui/ReferenceIdBadge";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { ActivityHistoryDrawer } from "@/components/audit/ActivityHistoryDrawer";
import { PhaseStagePicker } from "@/components/shared/PhaseStagePicker";
import { useAuth } from "@/modules/auth/hooks/useAuth";
import { useUserList } from "@/modules/users/hooks/useUsers";
import { useMeetingList } from "@/modules/meetings/hooks/useMeetings";
import { useIdeaList } from "@/modules/ideas/hooks/useIdeas";
import { useCompanyList } from "@/modules/companies/hooks/useCompanies";
import { useProductList } from "@/modules/products/hooks/useProducts";
import { useProjectList } from "@/modules/projects/hooks/useProjects";
import { useCategoryItems } from "@/modules/dropdowns/hooks/useDropdowns";
import { NotesTab } from "@/modules/notes/components/NotesTab";
import { IdeasTab } from "@/modules/ideas/components/IdeasTab";
import { DecisionsTab } from "@/modules/decisions/components/DecisionsTab";
import { TasksTabContent } from "@/modules/tasks/components/TasksTabContent";
import { ActionsTabContent } from "@/modules/actions/components/ActionsTabContent";
import { FileUploadZone } from "@/modules/media/components/FileUploadZone";
import {
  useAddMilestoneComment,
  useDeleteMilestone,
  useDeleteMilestoneComment,
  useMilestone,
  useMilestoneComments,
  useToggleMilestoneFlag,
  useUpdateMilestone,
  useUpdateMilestoneProgress,
  useUpdateMilestoneStatus,
} from "../hooks/useMilestones";
import { MilestoneStatusBadge } from "../components/MilestoneStatusBadge";
import { MilestonePriorityBadge } from "../components/MilestonePriorityBadge";
import type { MilestoneDetail } from "../types";

const STATUS_LIST: { value: string; label: string }[] = [
  { value: "not_started", label: "Not Started" },
  { value: "in_progress", label: "In Progress" },
  { value: "completed", label: "Completed" },
  { value: "at_risk", label: "At Risk" },
  { value: "delayed", label: "Delayed" },
  { value: "cancelled", label: "Cancelled" },
];

type ItemTab =
  | "all"
  | "tasks"
  | "actions"
  | "notes"
  | "ideas"
  | "decisions"
  | "comments";

const ITEM_TABS: { key: ItemTab; label: string }[] = [
  { key: "all", label: "All" },
  { key: "tasks", label: "Tasks" },
  { key: "actions", label: "Actions" },
  { key: "notes", label: "Notes" },
  { key: "ideas", label: "Ideas" },
  { key: "decisions", label: "Decisions" },
  { key: "comments", label: "Comments" },
];

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function formatDateTime(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function timeAgo(iso: string): string {
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} hr ago`;
  if (diff < 2592000) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}

function initialsOf(name: string): string {
  return name
    .split(" ")
    .map((n) => n[0])
    .filter(Boolean)
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

function isOverdue(m: MilestoneDetail): boolean {
  return (
    !!m.dueDate &&
    new Date(m.dueDate) < new Date() &&
    m.status !== "completed" &&
    m.status !== "cancelled"
  );
}

const STATUS_DOT: Record<string, string> = {
  not_started: "hsl(215,16%,60%)",
  in_progress: "hsl(210,100%,50%)",
  completed: "hsl(142,72%,40%)",
  at_risk: "hsl(32,90%,50%)",
  delayed: "hsl(0,70%,50%)",
  cancelled: "hsl(215,16%,60%)",
};

export default function MilestoneDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const { data: milestone, isLoading } = useMilestone(id ?? "");
  const updateMilestone = useUpdateMilestone(id ?? "");
  const updateStatus = useUpdateMilestoneStatus();
  const updateProgress = useUpdateMilestoneProgress();
  const toggleFlag = useToggleMilestoneFlag();
  const deleteMilestone = useDeleteMilestone();
  const commentsQuery = useMilestoneComments(id ?? "");
  const addComment = useAddMilestoneComment(id ?? "");
  const deleteComment = useDeleteMilestoneComment(id ?? "");
  const { data: usersResult } = useUserList({ page: 1, limit: 100 });
  const allUsers = usersResult?.data ?? [];
  const { data: meetingsResult } = useMeetingList({ page: 1, limit: 100 });
  const allMeetings = meetingsResult?.data ?? [];
  const { data: ideasResult } = useIdeaList({ page: 1, limit: 100 });
  const allIdeas = ideasResult?.data ?? [];
  const { data: companiesResult } = useCompanyList({
    page: 1,
    limit: 100,
    operationalOnly: true,
  });
  const allCompanies = companiesResult?.data ?? [];
  const [editCompanyId, setEditCompanyId] = useState<string>("");
  const [editProductId, setEditProductId] = useState<string>("");
  const [editProjectId, setEditProjectId] = useState<string>("");
  const { data: productsResult } = useProductList({
    page: 1,
    limit: 100,
    companyId: editCompanyId || undefined,
  });
  const allProducts = productsResult?.data ?? [];
  const { data: projectsResult } = useProjectList({
    page: 1,
    limit: 100,
    companyId: editCompanyId || undefined,
    productId: editProductId || undefined,
  });
  const allProjects = projectsResult?.data ?? [];
  const { data: categoryOptions = [] } = useCategoryItems("milestone_category");

  const [editMode, setEditMode] = useState(false);
  const [activityOpen, setActivityOpen] = useState(false);
  const [tab, setTab] = useState<ItemTab>("all");
  const [commentText, setCommentText] = useState("");
  const [toast, setToast] = useState<string | null>(null);

  const [editTitle, setEditTitle] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editCategory, setEditCategory] = useState("");
  const [editPriority, setEditPriority] = useState("medium");
  const [editStatus, setEditStatus] = useState("not_started");
  const [editIsFlagged, setEditIsFlagged] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [editOwnerId, setEditOwnerId] = useState<string>("");
  const [editPhaseId, setEditPhaseId] = useState<string | null>(null);
  const [editStageId, setEditStageId] = useState<string | null>(null);
  const [editMeetingId, setEditMeetingId] = useState<string>("");
  const [editIdeaId, setEditIdeaId] = useState<string>("");
  const [editStartDate, setEditStartDate] = useState("");
  const [editDueDate, setEditDueDate] = useState("");

  function enterEdit(m: MilestoneDetail) {
    setEditTitle(m.title);
    setEditDescription(m.description ?? "");
    setEditCategory(m.category ?? "");
    setEditPriority(m.priority);
    setEditStatus(m.status);
    setEditIsFlagged(m.isFlagged);
    setEditOwnerId(m.ownerId ?? "");
    setEditPhaseId(m.phaseId);
    setEditStageId(m.stageId);
    setEditMeetingId(m.meetingId ?? "");
    setEditIdeaId(m.ideaId ?? "");
    setEditCompanyId(m.companyId ?? "");
    setEditProductId(m.productId ?? "");
    setEditProjectId(m.projectId ?? "");
    setEditStartDate(m.startDate ? m.startDate.slice(0, 10) : "");
    setEditDueDate(m.dueDate ? m.dueDate.slice(0, 10) : "");
    setEditMode(true);
  }

  async function saveEdit() {
    await updateMilestone.mutateAsync({
      title: editTitle.trim(),
      description: editDescription.trim() || null,
      category: editCategory || null,
      priority: editPriority,
      status: editStatus,
      isFlagged: editIsFlagged,
      ownerId: editOwnerId || null,
      phaseId: editPhaseId,
      stageId: editStageId,
      meetingId: editMeetingId || null,
      ideaId: editIdeaId || null,
      companyId: editCompanyId || null,
      productId: editProductId || null,
      projectId: editProjectId || null,
      startDate: editStartDate ? new Date(editStartDate).toISOString() : null,
      dueDate: editDueDate ? new Date(editDueDate).toISOString() : null,
    });
    setEditMode(false);
    showToast("Milestone updated");
  }

  function showToast(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  }

  async function handleQuickStatus(newStatus: string) {
    if (!milestone) return;
    await updateStatus.mutateAsync({ id: milestone.id, status: newStatus });
    showToast(`Status changed`);
  }

  async function handleProgress(next: number) {
    if (!milestone) return;
    await updateProgress.mutateAsync({ id: milestone.id, progress: next });
  }

  async function handleToggleFlag() {
    if (!milestone) return;
    await toggleFlag.mutateAsync({
      id: milestone.id,
      isFlagged: !milestone.isFlagged,
    });
  }

  function handleDelete() {
    if (!milestone) return;
    setDeleteOpen(true);
  }

  async function confirmDelete() {
    if (!milestone) return;
    await deleteMilestone.mutateAsync(milestone.id);
    navigate("/milestones");
  }

  async function submitComment() {
    if (!commentText.trim()) return;
    await addComment.mutateAsync(commentText.trim());
    setCommentText("");
  }

  if (isLoading || !milestone) {
    return (
      <AppLayout
        breadcrumb={[
          { label: "PhazeX Hub", href: "/" },
          { label: "Daily Work" },
          { label: "Milestones", href: "/milestones" },
        ]}
      >
        <div className="py-12 text-center text-muted-foreground">
          {isLoading ? "Loading…" : "Milestone not found"}
        </div>
      </AppLayout>
    );
  }

  const overdue = isOverdue(milestone);

  const contextChain: {
    label: string;
    href?: string;
    icon: typeof Building2;
  }[] = [];
  if (milestone.companyId)
    contextChain.push({
      label: milestone.companyName ?? milestone.companyId,
      href: `/companies/${milestone.companyId}`,
      icon: Building2,
    });
  if (milestone.productId)
    contextChain.push({
      label: milestone.productName ?? milestone.productId,
      href: `/products/${milestone.productId}`,
      icon: Package,
    });
  if (milestone.projectId)
    contextChain.push({
      label: milestone.projectName ?? milestone.projectId,
      href: `/projects/${milestone.projectId}`,
      icon: FolderKanban,
    });
  if (milestone.phaseName)
    contextChain.push({ label: milestone.phaseName, icon: Layers });
  if (milestone.stageName)
    contextChain.push({ label: milestone.stageName, icon: GitBranch });
  if (milestone.meetingId)
    contextChain.push({
      label: milestone.meetingTitle ?? milestone.meetingId,
      href: `/meetings/${milestone.meetingId}`,
      icon: CalendarDays,
    });
  if (milestone.ideaId)
    contextChain.push({
      label: milestone.ideaTitle ?? milestone.ideaId,
      href: `/ideas/${milestone.ideaId}`,
      icon: Lightbulb,
    });

  return (
    <AppLayout
      breadcrumb={[
        { label: "PhazeX Hub", href: "/" },
        { label: "Daily Work" },
        { label: "Milestones", href: "/milestones" },
        { label: milestone.code },
      ]}
    >
      {toast && (
        <div
          className="fixed right-6 top-16 z-[9999] rounded-lg border bg-white px-4 py-3 text-sm font-medium text-green-700 shadow-lg"
          style={{ borderColor: "hsl(142,55%,80%)" }}
        >
          {toast}
        </div>
      )}

      {/* Page Header */}
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => navigate("/milestones")}
            className="flex h-8 w-8 items-center justify-center rounded hover:bg-[hsl(210,50%,96%)]"
          >
            <ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
          </button>
          <Flag
            className="h-[18px] w-[18px] text-foreground"
            strokeWidth={1.5}
          />
          <h1 className="text-xl font-semibold text-foreground">
            View Milestone
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleToggleFlag}
            className="flex h-8 w-8 items-center justify-center rounded hover:bg-[hsl(210,50%,96%)]"
            title={milestone.isFlagged ? "Unflag" : "Flag"}
          >
            <Flag
              className="h-4 w-4"
              strokeWidth={1.5}
              style={{
                color: milestone.isFlagged ? "hsl(40,95%,45%)" : undefined,
                fill: milestone.isFlagged ? "hsl(40,95%,55%)" : "none",
              }}
            />
          </button>
          <button
            type="button"
            onClick={() => setActivityOpen(true)}
            className="flex h-8 w-8 items-center justify-center rounded hover:bg-[hsl(210,50%,96%)]"
            title="History"
          >
            <History
              className="h-4 w-4 text-muted-foreground"
              strokeWidth={1.5}
            />
          </button>
          <button
            type="button"
            onClick={() => (editMode ? setEditMode(false) : enterEdit(milestone))}
            className="flex h-8 w-8 items-center justify-center rounded hover:bg-[hsl(210,50%,96%)]"
            title="Edit"
          >
            <Pencil
              className="h-4 w-4 text-muted-foreground"
              strokeWidth={1.5}
            />
          </button>
          <button
            type="button"
            onClick={handleDelete}
            className="flex h-8 w-8 items-center justify-center rounded hover:bg-[hsl(0,70%,96%)]"
            title="Delete"
          >
            <Trash2
              className="h-4 w-4 text-[hsl(0,70%,45%)]"
              strokeWidth={1.5}
            />
          </button>
        </div>
      </div>

      {/* ROW 1 — Summary Header */}
      <div
        className="mb-4 rounded-xl border bg-white"
        style={{ borderColor: "hsl(210,30%,85%)", padding: "16px 20px" }}
      >
        {editMode ? (
          <Input
            value={editTitle}
            onChange={(e) => setEditTitle(e.target.value)}
            className="text-[20px] font-bold"
          />
        ) : (
          <div className="inline-flex items-center gap-2 text-[20px] font-bold text-foreground">
            {milestone.isFlagged && (
              <Flag
                className="h-4 w-4"
                strokeWidth={1.5}
                style={{
                  color: "hsl(40,95%,45%)",
                  fill: "hsl(40,95%,55%)",
                }}
              />
            )}
            {milestone.title}
          </div>
        )}
        <div className="mt-2 flex flex-wrap items-center gap-2 text-[13px] text-muted-foreground">
          <ReferenceIdBadge id={milestone.code} />
          <MilestoneStatusBadge status={milestone.status} />
          <MilestonePriorityBadge priority={milestone.priority} />
          {milestone.category && (
            <span
              className="inline-flex rounded-full px-2 py-0.5 text-[11px] capitalize"
              style={{ background: "hsl(210,16%,95%)" }}
            >
              {milestone.category.replace(/_/g, " ")}
            </span>
          )}
          {milestone.projectName && (
            <span className="inline-flex items-center gap-1">
              <FolderKanban className="h-3 w-3" strokeWidth={1.5} />
              {milestone.projectName}
            </span>
          )}
          {milestone.ownerName && (
            <span className="inline-flex items-center gap-1">
              <UserIcon className="h-3 w-3" strokeWidth={1.5} />
              {milestone.ownerName}
            </span>
          )}
          {milestone.dueDate && (
            <span
              className={`inline-flex items-center gap-1 ${
                overdue ? "text-[hsl(0,70%,45%)] font-medium" : ""
              }`}
            >
              <Calendar className="h-3 w-3" strokeWidth={1.5} />
              {formatDate(milestone.dueDate)}
            </span>
          )}
          {overdue && (
            <span
              className="rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase"
              style={{
                background: "hsl(0,70%,94%)",
                color: "hsl(0,70%,40%)",
              }}
            >
              Overdue
            </span>
          )}
        </div>

        <div
          className="mt-3 flex flex-wrap items-center gap-1.5 text-[12px] text-muted-foreground"
        >
          <span className="font-medium">PhazeX</span>
          {contextChain.length === 0 ? (
            <>
              <ChevronRight className="h-3 w-3" strokeWidth={1.5} />
              <span>—</span>
            </>
          ) : (
            contextChain.map((c, i) => {
              const Icon = c.icon;
              return (
                <span key={i} className="inline-flex items-center gap-1.5">
                  <ChevronRight className="h-3 w-3" strokeWidth={1.5} />
                  <Icon className="h-3 w-3" strokeWidth={1.5} />
                  {c.href ? (
                    <Link
                      to={c.href}
                      className="hover:text-[hsl(210,100%,35%)]"
                    >
                      {c.label}
                    </Link>
                  ) : (
                    <span>{c.label}</span>
                  )}
                </span>
              );
            })
          )}
        </div>

        <div
          className="mt-3 rounded-lg border bg-[hsl(210,30%,98%)] p-3"
          style={{ borderColor: "hsl(210,30%,88%)" }}
        >
          <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            Linked Context
          </div>
          <div className="grid grid-cols-2 gap-y-1.5 text-[12px]">
            <LinkedRow
              label="Company"
              value={milestone.companyName}
              href={
                milestone.companyId
                  ? `/companies/${milestone.companyId}`
                  : null
              }
            />
            <LinkedRow
              label="Product"
              value={milestone.productName}
              href={
                milestone.productId
                  ? `/products/${milestone.productId}`
                  : null
              }
            />
            <LinkedRow
              label="Project"
              value={milestone.projectName}
              href={
                milestone.projectId
                  ? `/projects/${milestone.projectId}`
                  : null
              }
            />
            <LinkedRow
              label="Meeting"
              value={
                milestone.meetingTitle
                  ? `${milestone.meetingCode ?? ""} ${milestone.meetingTitle}`.trim()
                  : null
              }
              href={
                milestone.meetingId
                  ? `/meetings/${milestone.meetingId}`
                  : null
              }
            />
            <LinkedRow
              label="Parent Idea"
              value={
                milestone.ideaTitle
                  ? `${milestone.ideaCode ?? ""} ${milestone.ideaTitle}`.trim()
                  : null
              }
              href={
                milestone.ideaId ? `/ideas/${milestone.ideaId}` : null
              }
            />
          </div>
        </div>

        <div className="mt-4">
          <div
            style={{
              width: "100%",
              height: 12,
              background: "hsl(210,16%,93%)",
              borderRadius: 999,
              overflow: "hidden",
            }}
          >
            <div
              style={{
                width: `${milestone.progress}%`,
                height: "100%",
                background: STATUS_DOT[milestone.status] ?? "hsl(210,100%,50%)",
                borderRadius: 999,
                transition: "width 200ms ease",
              }}
            />
          </div>
          <div className="mt-2 flex items-center justify-between text-[12px]">
            <span className="text-foreground">
              {milestone.progress}% complete
            </span>
            <span className="text-muted-foreground">
              Due: {formatDate(milestone.dueDate)}
            </span>
          </div>
          <div className="mt-1 flex items-center gap-2 text-[12px] text-muted-foreground">
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: 999,
                background:
                  STATUS_DOT[milestone.status] ?? "hsl(215,16%,60%)",
                display: "inline-block",
              }}
            />
            <span className="capitalize">
              {milestone.status.replace(/_/g, " ")}
            </span>
          </div>
        </div>
      </div>

      {/* ROW 2 — Details + Status/Dates */}
      <div
        className="mb-4 grid gap-4"
        style={{ gridTemplateColumns: "60fr 40fr" }}
      >
        {/* LEFT */}
        <div
          className="rounded-xl border bg-white"
          style={{ borderColor: "hsl(210,30%,85%)", padding: "16px 20px" }}
        >
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-[13px] font-semibold text-foreground">
              Details
            </h3>
            {editMode ? (
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={saveEdit}
                  className="flex h-7 w-7 items-center justify-center rounded text-[hsl(142,72%,28%)] hover:bg-[hsl(142,55%,94%)]"
                  title="Save"
                >
                  <Check className="h-4 w-4" strokeWidth={1.5} />
                </button>
                <button
                  type="button"
                  onClick={() => setEditMode(false)}
                  className="flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-[hsl(210,50%,96%)]"
                  title="Cancel"
                >
                  <X className="h-4 w-4" strokeWidth={1.5} />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => enterEdit(milestone)}
                className="flex h-7 w-7 items-center justify-center rounded hover:bg-[hsl(210,50%,96%)]"
                title="Edit"
              >
                <Pencil
                  className="h-3.5 w-3.5 text-muted-foreground"
                  strokeWidth={1.5}
                />
              </button>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <div className="text-[11px] uppercase text-muted-foreground">
                Category
              </div>
              {editMode ? (
                <Select
                  value={editCategory}
                  onChange={(e) => setEditCategory(e.target.value)}
                >
                  <option value="">No category</option>
                  {categoryOptions.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </Select>
              ) : (
                <div className="mt-0.5 capitalize text-foreground">
                  {milestone.category?.replace(/_/g, " ") ?? "—"}
                </div>
              )}
            </div>
            <div>
              <div className="text-[11px] uppercase text-muted-foreground">
                Priority
              </div>
              {editMode ? (
                <Select
                  value={editPriority}
                  onChange={(e) => setEditPriority(e.target.value)}
                >
                  <option value="critical">Critical</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </Select>
              ) : (
                <div className="mt-1">
                  <MilestonePriorityBadge priority={milestone.priority} />
                </div>
              )}
            </div>
            <div>
              <div className="text-[11px] uppercase text-muted-foreground">
                Status
              </div>
              {editMode ? (
                <Select
                  value={editStatus}
                  onChange={(e) => setEditStatus(e.target.value)}
                >
                  {STATUS_LIST.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </Select>
              ) : (
                <div className="mt-1">
                  <MilestoneStatusBadge status={milestone.status} />
                </div>
              )}
            </div>
            <div>
              <div className="text-[11px] uppercase text-muted-foreground">
                Flagged
              </div>
              {editMode ? (
                <button
                  type="button"
                  onClick={() => setEditIsFlagged((v) => !v)}
                  className="mt-1 inline-flex h-8 items-center gap-1 rounded-md border px-2 text-xs"
                  style={{
                    borderColor: editIsFlagged
                      ? "hsl(40,95%,60%)"
                      : "hsl(210,30%,85%)",
                    background: editIsFlagged ? "hsl(40,95%,95%)" : "white",
                  }}
                >
                  <Flag
                    className="h-3 w-3"
                    strokeWidth={1.5}
                    style={{
                      color: editIsFlagged ? "hsl(40,95%,45%)" : undefined,
                      fill: editIsFlagged ? "hsl(40,95%,55%)" : "none",
                    }}
                  />
                  {editIsFlagged ? "Flagged" : "Not flagged"}
                </button>
              ) : (
                <div className="mt-0.5 text-foreground">
                  {milestone.isFlagged ? "Yes" : "No"}
                </div>
              )}
            </div>
            <div>
              <div className="text-[11px] uppercase text-muted-foreground">
                Start Date
              </div>
              {editMode ? (
                <Input
                  type="date"
                  value={editStartDate}
                  onChange={(e) => setEditStartDate(e.target.value)}
                />
              ) : (
                <div className="mt-0.5 text-foreground">
                  {formatDate(milestone.startDate)}
                </div>
              )}
            </div>
            <div>
              <div className="text-[11px] uppercase text-muted-foreground">
                Due Date
              </div>
              {editMode ? (
                <Input
                  type="date"
                  value={editDueDate}
                  onChange={(e) => setEditDueDate(e.target.value)}
                />
              ) : (
                <div
                  className={`mt-0.5 ${
                    overdue
                      ? "text-[hsl(0,70%,45%)] font-medium"
                      : "text-foreground"
                  }`}
                >
                  {formatDate(milestone.dueDate)}
                </div>
              )}
            </div>
            {milestone.completedAt && (
              <div className="col-span-2">
                <div className="text-[11px] uppercase text-muted-foreground">
                  Completed At
                </div>
                <div className="mt-0.5 text-foreground">
                  {formatDateTime(milestone.completedAt)}
                </div>
              </div>
            )}
            {editMode && (
              <div>
                <div className="text-[11px] uppercase text-muted-foreground">
                  Company
                </div>
                <select
                  value={editCompanyId}
                  onChange={(e) => {
                    setEditCompanyId(e.target.value);
                    setEditProductId("");
                    setEditProjectId("");
                    setEditPhaseId(null);
                    setEditStageId(null);
                  }}
                  className="mt-1 flex h-9 w-full rounded-md border border-border bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                >
                  <option value="">No company</option>
                  {allCompanies.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name} ({c.code})
                    </option>
                  ))}
                </select>
              </div>
            )}
            {editMode && (
              <div>
                <div className="text-[11px] uppercase text-muted-foreground">
                  Product
                </div>
                <select
                  value={editProductId}
                  onChange={(e) => {
                    setEditProductId(e.target.value);
                    setEditProjectId("");
                    setEditPhaseId(null);
                    setEditStageId(null);
                  }}
                  className="mt-1 flex h-9 w-full rounded-md border border-border bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                >
                  <option value="">No product</option>
                  {allProducts.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} ({p.code})
                    </option>
                  ))}
                </select>
              </div>
            )}
            {editMode && (
              <div>
                <div className="text-[11px] uppercase text-muted-foreground">
                  Project
                </div>
                <select
                  value={editProjectId}
                  onChange={(e) => {
                    setEditProjectId(e.target.value);
                    setEditPhaseId(null);
                    setEditStageId(null);
                  }}
                  className="mt-1 flex h-9 w-full rounded-md border border-border bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                >
                  <option value="">No project</option>
                  {allProjects.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} ({p.code})
                    </option>
                  ))}
                </select>
              </div>
            )}
            {editMode && (
              <div className="col-span-2">
                <div className="text-[11px] uppercase text-muted-foreground">
                  Phase / Stage
                </div>
                <PhaseStagePicker
                  projectId={editProjectId || null}
                  phaseId={editPhaseId}
                  stageId={editStageId}
                  onPhaseChange={setEditPhaseId}
                  onStageChange={setEditStageId}
                />
              </div>
            )}
            {editMode && (
              <div>
                <div className="text-[11px] uppercase text-muted-foreground">
                  Meeting
                </div>
                <select
                  value={editMeetingId}
                  onChange={(e) => setEditMeetingId(e.target.value)}
                  className="mt-1 flex h-9 w-full rounded-md border border-border bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                >
                  <option value="">No meeting</option>
                  {allMeetings.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.code} — {m.title}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {editMode && (
              <div>
                <div className="text-[11px] uppercase text-muted-foreground">
                  Parent Idea
                </div>
                <select
                  value={editIdeaId}
                  onChange={(e) => setEditIdeaId(e.target.value)}
                  className="mt-1 flex h-9 w-full rounded-md border border-border bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                >
                  <option value="">No parent idea</option>
                  {allIdeas.map((i) => (
                    <option key={i.id} value={i.id}>
                      {i.code} — {i.title}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <div className="mt-4">
            <div className="text-[11px] uppercase text-muted-foreground">
              Description
            </div>
            {editMode ? (
              <textarea
                rows={3}
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                className="mt-1 flex w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              />
            ) : (
              <div className="mt-1 whitespace-pre-wrap text-sm text-foreground">
                {milestone.description ?? "—"}
              </div>
            )}
          </div>

          {!editMode && (
            <div
              className="mt-4 border-t pt-4"
              style={{ borderColor: "hsl(210,30%,90%)" }}
            >
              <div className="mb-2 flex items-center justify-between">
                <div className="text-[11px] uppercase text-muted-foreground">
                  Progress
                </div>
                <div className="text-sm font-semibold text-foreground">
                  {milestone.progress}%
                </div>
              </div>
              <input
                type="range"
                min={0}
                max={100}
                value={milestone.progress}
                onChange={(e) => handleProgress(Number(e.target.value))}
                style={{
                  width: "100%",
                  accentColor: "hsl(210,100%,45%)",
                  height: 6,
                }}
              />
              <div className="mt-2 flex flex-wrap gap-2">
                {[0, 25, 50, 75, 100].map((v) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => handleProgress(v)}
                    className="inline-flex h-7 items-center rounded-full border px-3 text-[12px] font-medium transition-colors"
                    style={{
                      background:
                        milestone.progress === v
                          ? "hsl(210,100%,45%)"
                          : "transparent",
                      color:
                        milestone.progress === v ? "#fff" : "hsl(215,16%,40%)",
                      borderColor:
                        milestone.progress === v
                          ? "hsl(210,100%,45%)"
                          : "hsl(210,30%,85%)",
                    }}
                  >
                    {v}%
                  </button>
                ))}
              </div>
            </div>
          )}

          {editMode && (
            <div className="mt-4">
              <div className="text-[11px] uppercase text-muted-foreground">
                Owner
              </div>
              <select
                value={editOwnerId}
                onChange={(e) => setEditOwnerId(e.target.value)}
                className="mt-1 flex h-8 w-full rounded-md border border-border bg-background px-2 py-1 text-sm"
              >
                <option value="">Unassigned</option>
                {allUsers.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* RIGHT */}
        <div
          className="rounded-xl border bg-white"
          style={{ borderColor: "hsl(210,30%,85%)", padding: "16px 20px" }}
        >
          <div className="mb-2 text-[11px] uppercase text-muted-foreground">
            Change Status
          </div>
          <div className="flex flex-wrap gap-1.5">
            {STATUS_LIST.map((s) => {
              const active = milestone.status === s.value;
              return (
                <button
                  key={s.value}
                  type="button"
                  disabled={updateStatus.isPending}
                  onClick={() => handleQuickStatus(s.value)}
                  className="inline-flex h-6 items-center rounded-full border px-2.5 text-[11px] font-medium transition-colors"
                  style={{
                    background: active
                      ? "hsl(210,100%,45%)"
                      : "transparent",
                    color: active ? "#fff" : "hsl(215,16%,40%)",
                    borderColor: active
                      ? "hsl(210,100%,45%)"
                      : "hsl(210,30%,85%)",
                  }}
                >
                  {s.label}
                </button>
              );
            })}
          </div>

          <div
            className="my-4 border-t"
            style={{ borderColor: "hsl(210,30%,90%)" }}
          />

          <h3 className="mb-3 text-[13px] font-semibold text-foreground">
            Dates
          </h3>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <div className="text-[11px] uppercase text-muted-foreground">
                Start Date
              </div>
              <div className="mt-0.5 text-foreground">
                {formatDate(milestone.startDate)}
              </div>
            </div>
            <div>
              <div className="text-[11px] uppercase text-muted-foreground">
                Due Date
              </div>
              <div
                className={`mt-0.5 ${
                  overdue
                    ? "text-[hsl(0,70%,45%)] font-medium"
                    : "text-foreground"
                }`}
              >
                {formatDate(milestone.dueDate)}
              </div>
            </div>
            <div>
              <div className="text-[11px] uppercase text-muted-foreground">
                Created At
              </div>
              <div className="mt-0.5 text-foreground">
                {formatDateTime(milestone.createdAt)}
              </div>
            </div>
            <div>
              <div className="text-[11px] uppercase text-muted-foreground">
                Updated At
              </div>
              <div className="mt-0.5 text-foreground">
                {formatDateTime(milestone.updatedAt)}
              </div>
            </div>
            {milestone.completedAt && (
              <div className="col-span-2">
                <div className="text-[11px] uppercase text-muted-foreground">
                  Completed At
                </div>
                <div className="mt-0.5 text-foreground">
                  {formatDateTime(milestone.completedAt)}
                </div>
              </div>
            )}
          </div>

          <div
            className="my-4 border-t"
            style={{ borderColor: "hsl(210,30%,90%)" }}
          />

          <div className="mb-2 text-[11px] uppercase text-muted-foreground">
            Owner
          </div>
          {milestone.ownerName ? (
            <div className="flex items-center gap-2">
              <span
                className="flex h-8 w-8 items-center justify-center rounded-full text-[11px] font-semibold"
                style={{
                  background: "hsl(210,55%,92%)",
                  color: "hsl(210,100%,28%)",
                }}
              >
                {initialsOf(milestone.ownerName)}
              </span>
              <div className="text-sm text-foreground">
                {milestone.ownerName}
              </div>
            </div>
          ) : (
            <div className="text-sm text-muted-foreground">Unassigned</div>
          )}
        </div>
      </div>

      {/* ROW 3 — Tabs */}
      <div
        className="mb-4 overflow-hidden rounded-xl border bg-white"
        style={{ borderColor: "hsl(210,30%,85%)" }}
      >
        <div
          className="flex items-center border-b"
          style={{ borderColor: "hsl(210,30%,90%)" }}
        >
          {ITEM_TABS.map((t) => {
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                className={`flex items-center gap-1.5 border-b-2 px-4 py-2.5 text-sm font-medium transition-colors ${
                  active
                    ? "border-primary text-primary"
                    : "border-transparent text-muted-foreground"
                }`}
              >
                {t.label}
              </button>
            );
          })}
        </div>

        <div className="p-4">
          {tab === "all" && (
            <div className="py-8 text-center text-sm text-muted-foreground">
              Use the tabs above to add tasks, actions, notes, ideas,
              decisions, or comments.
            </div>
          )}

          {tab === "tasks" && (
            <TasksTabContent
              milestoneId={milestone.id}
              prefill={{
                companyId: milestone.companyId,
                productId: milestone.productId,
                projectId: milestone.projectId,
                milestoneId: milestone.id,
                milestoneTitle: milestone.title,
                locked: { milestoneId: true },
              }}
            />
          )}

          {tab === "actions" && (
            <ActionsTabContent
              milestoneId={milestone.id}
              prefill={{
                companyId: milestone.companyId,
                productId: milestone.productId,
                projectId: milestone.projectId,
                milestoneId: milestone.id,
                milestoneTitle: milestone.title,
                locked: { milestoneId: true },
              }}
            />
          )}

          {tab === "notes" &&
            (milestone.projectId ? (
              <NotesTab
                filter={{ projectId: milestone.projectId }}
                prefill={{
                  companyId: milestone.companyId,
                  productId: milestone.productId,
                  projectId: milestone.projectId,
                }}
              />
            ) : milestone.companyId ? (
              <NotesTab
                filter={{ companyId: milestone.companyId }}
                prefill={{
                  companyId: milestone.companyId,
                  productId: milestone.productId,
                  projectId: milestone.projectId,
                }}
              />
            ) : (
              <div className="py-8 text-center text-sm text-muted-foreground">
                Attach a company or project to view notes.
              </div>
            ))}

          {tab === "ideas" &&
            (milestone.projectId ? (
              <IdeasTab
                filter={{ projectId: milestone.projectId }}
                prefill={{
                  companyId: milestone.companyId,
                  productId: milestone.productId,
                  projectId: milestone.projectId,
                }}
              />
            ) : milestone.companyId ? (
              <IdeasTab
                filter={{ companyId: milestone.companyId }}
                prefill={{
                  companyId: milestone.companyId,
                  productId: milestone.productId,
                  projectId: milestone.projectId,
                }}
              />
            ) : (
              <div className="py-8 text-center text-sm text-muted-foreground">
                Attach a company or project to view ideas.
              </div>
            ))}

          {tab === "decisions" &&
            (milestone.projectId ? (
              <DecisionsTab
                filter={{ projectId: milestone.projectId }}
                prefill={{
                  companyId: milestone.companyId,
                  productId: milestone.productId,
                  projectId: milestone.projectId,
                }}
              />
            ) : milestone.companyId ? (
              <DecisionsTab
                filter={{ companyId: milestone.companyId }}
                prefill={{
                  companyId: milestone.companyId,
                  productId: milestone.productId,
                  projectId: milestone.projectId,
                }}
              />
            ) : (
              <div className="py-8 text-center text-sm text-muted-foreground">
                Attach a company or project to view decisions.
              </div>
            ))}

          {tab === "comments" && (
            <div className="py-4 text-center text-sm text-muted-foreground">
              See the Comments section below.
            </div>
          )}
        </div>
      </div>

      {/* ROW 4 — Comments + Attachments */}
      <div
        className="grid gap-4"
        style={{ gridTemplateColumns: "60fr 40fr" }}
      >
        <div
          className="flex flex-col rounded-xl border bg-white"
          style={{ borderColor: "hsl(210,30%,85%)", padding: "16px 20px" }}
        >
          <div className="mb-3 flex items-center gap-2">
            <MessageSquare
              className="h-4 w-4 text-foreground"
              strokeWidth={1.5}
            />
            <h3 className="text-[13px] font-semibold text-foreground">
              Comments
            </h3>
            <span
              className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-[10px] font-medium"
              style={{
                background: "hsl(210,16%,93%)",
                color: "hsl(215,16%,40%)",
              }}
            >
              {commentsQuery.data?.length ?? 0}
            </span>
          </div>

          <div className="max-h-[300px] flex-1 overflow-y-auto">
            {(!commentsQuery.data || commentsQuery.data.length === 0) && (
              <div className="py-6 text-center text-sm text-muted-foreground">
                No comments yet. Be the first to comment.
              </div>
            )}
            {[...(commentsQuery.data ?? [])].reverse().map((c) => {
              const isOwn = c.authorId === user?.id;
              return (
                <div
                  key={c.id}
                  className="border-b py-3 last:border-b-0"
                  style={{ borderColor: "hsl(210,30%,93%)" }}
                >
                  <div className="flex items-start gap-2">
                    <span
                      className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold"
                      style={{
                        background: "hsl(210,55%,92%)",
                        color: "hsl(210,100%,28%)",
                      }}
                    >
                      {initialsOf(c.authorName ?? "??")}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-[13px] font-semibold text-foreground">
                          {c.authorName ?? "Unknown"}
                        </span>
                        <span className="text-[11px] text-muted-foreground">
                          <Clock
                            className="mr-1 inline h-3 w-3"
                            strokeWidth={1.5}
                          />
                          {timeAgo(c.createdAt)}
                        </span>
                        {isOwn && (
                          <button
                            type="button"
                            onClick={() => deleteComment.mutate(c.id)}
                            className="ml-auto text-muted-foreground hover:text-[hsl(0,70%,45%)]"
                            title="Delete"
                          >
                            <Trash2 className="h-3 w-3" strokeWidth={1.5} />
                          </button>
                        )}
                      </div>
                      <div className="mt-1 whitespace-pre-wrap text-[14px] text-foreground">
                        {c.content}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div
            className="mt-3 flex items-end gap-2 border-t pt-3"
            style={{ borderColor: "hsl(210,30%,90%)" }}
          >
            <textarea
              rows={2}
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              onKeyDown={(e) => {
                if (e.ctrlKey && e.key === "Enter") {
                  e.preventDefault();
                  submitComment();
                }
              }}
              placeholder="Write a comment... (Ctrl+Enter to submit)"
              className="flex-1 rounded-md border border-border bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
            <button
              type="button"
              onClick={submitComment}
              disabled={!commentText.trim() || addComment.isPending}
              className="flex h-9 w-9 items-center justify-center rounded-full text-white disabled:opacity-50"
              style={{ background: "hsl(210,100%,45%)" }}
              title="Send"
            >
              <Send className="h-3.5 w-3.5" strokeWidth={1.8} />
            </button>
          </div>
        </div>

        <div
          className="rounded-xl border bg-white"
          style={{ borderColor: "hsl(210,30%,85%)", padding: "16px 20px" }}
        >
          <div className="mb-3 flex items-center gap-2">
            <Paperclip
              className="h-4 w-4 text-foreground"
              strokeWidth={1.5}
            />
            <h3 className="text-[13px] font-semibold text-foreground">
              Attachments
            </h3>
          </div>
          <FileUploadZone
            entityType="milestone"
            entityId={milestone.id}
            entityLabel={milestone.code}
            milestoneId={milestone.id}
            companyId={milestone.companyId ?? undefined}
            productId={milestone.productId ?? undefined}
            projectId={milestone.projectId ?? undefined}
            meetingId={milestone.meetingId ?? undefined}
            showExisting
            compact
          />
        </div>
      </div>

      <ActivityHistoryDrawer
        isOpen={activityOpen}
        onClose={() => setActivityOpen(false)}
        entityType="milestone"
        entityId={milestone.id}
        entityLabel={milestone.title}
      />

      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title="Delete milestone?"
        description={`Are you sure you want to delete '${milestone.title}'? It will be removed from your Milestones list.`}
        onConfirm={confirmDelete}
      />
    </AppLayout>
  );
}

function LinkedRow({
  label,
  value,
  href,
}: {
  label: string;
  value: string | null;
  href?: string | null;
}) {
  return (
    <div className="flex gap-2">
      <span className="w-24 shrink-0 text-muted-foreground">{label}:</span>
      {value ? (
        href ? (
          <Link to={href} className="text-foreground hover:underline">
            {value}
          </Link>
        ) : (
          <span className="text-foreground">{value}</span>
        )
      ) : (
        <span className="text-muted-foreground">—</span>
      )}
    </div>
  );
}
